import React, { useEffect, useState, useRef } from 'react';
import { X, ArrowRight, ArrowLeft, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useTour } from '../../contexts/TourContext';
import { cn } from '../../lib/utils';

interface TourOverlayProps {
  onNavigate: (page: string) => void;
}

interface Position {
  top: number;
  left: number;
}

export const TourOverlay: React.FC<TourOverlayProps> = ({ onNavigate }) => {
  const { activeTour, currentStepIndex, nextStep, previousStep, skipTour } = useTour();
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [modalPosition, setModalPosition] = useState<Position>({ top: 0, left: 0 });
  const [optimalPosition, setOptimalPosition] = useState<Position>({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('bottom');
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ top: 0, left: 0 });
  const [isUserPositioned, setIsUserPositioned] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const currentStep = activeTour?.steps[currentStepIndex];

  useEffect(() => {
    if (!currentStep) return;

    // Handle navigation
    if (currentStep.navigateTo) {
      onNavigate(currentStep.navigateTo);
    }

    // Handle custom action
    if (currentStep.action) {
      currentStep.action();
    }

    // Find and highlight target element
    if (currentStep.target) {
      // Wait for DOM to update after navigation
      const findAndPositionElement = () => {
        const element = document.querySelector(currentStep.target!) as HTMLElement;
        if (element) {
          setTargetElement(element);
          
          // Scroll element into view smoothly
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center', 
            inline: 'center' 
          });
          
          // Calculate modal position after scroll completes
          setTimeout(() => {
            calculateModalPosition(element);
          }, 500);
        } else {
          // Element not found, try again after a delay
          setTimeout(findAndPositionElement, 200);
        }
      };

      // Initial delay to allow navigation to complete
      setTimeout(findAndPositionElement, 150);
    } else {
      setTargetElement(null);
      // Center modal when no target
      setModalPosition({ top: 0, left: 0 });
    }
  }, [currentStep, onNavigate]);

  const calculateModalPosition = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const modalWidth = 400;
    const modalHeight = 300; // Approximate
    const padding = 20;
    const arrowSize = 12;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;
    let arrow: 'top' | 'bottom' | 'left' | 'right' = 'bottom';

    // Determine position based on available space and step.position hint
    const position = currentStep?.position || 'bottom';

    // Calculate space in each direction
    const spaceTop = rect.top;
    const spaceBottom = viewportHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;

    // Position logic based on hint and available space
    if (position.includes('bottom') || (position === 'bottom' && spaceBottom > modalHeight + padding)) {
      // Position below element
      top = rect.bottom + padding + arrowSize;
      left = rect.left + rect.width / 2 - modalWidth / 2;
      arrow = 'top';
    } else if (position.includes('top') || (position === 'top' && spaceTop > modalHeight + padding)) {
      // Position above element
      top = rect.top - modalHeight - padding - arrowSize;
      left = rect.left + rect.width / 2 - modalWidth / 2;
      arrow = 'bottom';
    } else if (position.includes('right') || (position === 'right' && spaceRight > modalWidth + padding)) {
      // Position to the right
      top = rect.top + rect.height / 2 - modalHeight / 2;
      left = rect.right + padding + arrowSize;
      arrow = 'left';
    } else if (position.includes('left') || (position === 'left' && spaceLeft > modalWidth + padding)) {
      // Position to the left
      top = rect.top + rect.height / 2 - modalHeight / 2;
      left = rect.left - modalWidth - padding - arrowSize;
      arrow = 'right';
    } else {
      // Fallback: position where there's most space
      const maxSpace = Math.max(spaceTop, spaceBottom, spaceLeft, spaceRight);
      if (maxSpace === spaceBottom) {
        top = rect.bottom + padding + arrowSize;
        left = rect.left + rect.width / 2 - modalWidth / 2;
        arrow = 'top';
      } else if (maxSpace === spaceTop) {
        top = rect.top - modalHeight - padding - arrowSize;
        left = rect.left + rect.width / 2 - modalWidth / 2;
        arrow = 'bottom';
      } else if (maxSpace === spaceRight) {
        top = rect.top + rect.height / 2 - modalHeight / 2;
        left = rect.right + padding + arrowSize;
        arrow = 'left';
      } else {
        top = rect.top + rect.height / 2 - modalHeight / 2;
        left = rect.left - modalWidth - padding - arrowSize;
        arrow = 'right';
      }
    }

    // Ensure modal stays within viewport
    if (left < padding) left = padding;
    if (left + modalWidth > viewportWidth - padding) left = viewportWidth - modalWidth - padding;
    if (top < padding) top = padding;
    if (top + modalHeight > viewportHeight - padding) top = viewportHeight - modalHeight - padding;

    // Store optimal position
    setOptimalPosition({ top, left });
    
    // Only update modal position if user hasn't manually positioned it
    if (!isUserPositioned) {
      setModalPosition({ top, left });
    }
    
    setArrowPosition(arrow);
  };

  // Reset to optimal position when step changes
  useEffect(() => {
    setIsUserPositioned(false);
    stopSpeech();
  }, [currentStepIndex]);

  // Animate back to optimal position
  const animateToOptimalPosition = () => {
    setIsUserPositioned(false);
    setModalPosition(optimalPosition);
  };

  // Dragging handlers
  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    // Only start dragging if clicking on the header itself, not buttons
    if (e.target instanceof HTMLElement && 
        (e.target.tagName === 'BUTTON' || e.target.closest('button'))) {
      return;
    }
    
    e.preventDefault();
    setIsDragging(true);
    setDragOffset({
      top: e.clientY - modalPosition.top,
      left: e.clientX - modalPosition.left,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      
      e.preventDefault();
      
      const newTop = e.clientY - dragOffset.top;
      const newLeft = e.clientX - dragOffset.left;

      // Keep modal within viewport
      const modalWidth = modalRef.current?.offsetWidth || 400;
      const modalHeight = modalRef.current?.offsetHeight || 300;
      const padding = 20;

      const boundedTop = Math.max(padding, Math.min(newTop, window.innerHeight - modalHeight - padding));
      const boundedLeft = Math.max(padding, Math.min(newLeft, window.innerWidth - modalWidth - padding));

      setModalPosition({ top: boundedTop, left: boundedLeft });
      setIsUserPositioned(true);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Prevent text selection while dragging
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, dragOffset]);

  // Convert text to natural speech format
  const convertToNaturalSpeech = (text: string): string => {
    let naturalText = text;
    
    // Convert currency amounts to natural speech
    // £50K -> "fifty thousand pounds"
    naturalText = naturalText.replace(/£50K/gi, 'fifty thousand pounds');
    naturalText = naturalText.replace(/£50k/gi, 'fifty thousand pounds');
    
    // £247K -> "two hundred and forty-seven thousand pounds"
    naturalText = naturalText.replace(/£247K/gi, 'two hundred and forty-seven thousand pounds');
    naturalText = naturalText.replace(/£247k/gi, 'two hundred and forty-seven thousand pounds');
    
    // £22K -> "twenty-two thousand pounds"
    naturalText = naturalText.replace(/£22K/gi, 'twenty-two thousand pounds');
    naturalText = naturalText.replace(/£22k/gi, 'twenty-two thousand pounds');
    
    // £63K -> "sixty-three thousand pounds"
    naturalText = naturalText.replace(/£63K/gi, 'sixty-three thousand pounds');
    naturalText = naturalText.replace(/£63k/gi, 'sixty-three thousand pounds');
    
    // £45K -> "forty-five thousand pounds"
    naturalText = naturalText.replace(/£45K/gi, 'forty-five thousand pounds');
    naturalText = naturalText.replace(/£45k/gi, 'forty-five thousand pounds');
    
    // £85K -> "eighty-five thousand pounds"
    naturalText = naturalText.replace(/£85K/gi, 'eighty-five thousand pounds');
    naturalText = naturalText.replace(/£85k/gi, 'eighty-five thousand pounds');
    
    // £125K -> "one hundred and twenty-five thousand pounds"
    naturalText = naturalText.replace(/£125K/gi, 'one hundred and twenty-five thousand pounds');
    naturalText = naturalText.replace(/£125k/gi, 'one hundred and twenty-five thousand pounds');
    
    // £150K -> "one hundred and fifty thousand pounds"
    naturalText = naturalText.replace(/£150K/gi, 'one hundred and fifty thousand pounds');
    naturalText = naturalText.replace(/£150k/gi, 'one hundred and fifty thousand pounds');
    
    // £285K -> "two hundred and eighty-five thousand pounds"
    naturalText = naturalText.replace(/£285K/gi, 'two hundred and eighty-five thousand pounds');
    naturalText = naturalText.replace(/£285k/gi, 'two hundred and eighty-five thousand pounds');
    
    // £380K -> "three hundred and eighty thousand pounds"
    naturalText = naturalText.replace(/£380K/gi, 'three hundred and eighty thousand pounds');
    naturalText = naturalText.replace(/£380k/gi, 'three hundred and eighty thousand pounds');
    
    // £38K -> "thirty-eight thousand pounds"
    naturalText = naturalText.replace(/£38K/gi, 'thirty-eight thousand pounds');
    naturalText = naturalText.replace(/£38k/gi, 'thirty-eight thousand pounds');
    
    // £15K -> "fifteen thousand pounds"
    naturalText = naturalText.replace(/£15K/gi, 'fifteen thousand pounds');
    naturalText = naturalText.replace(/£15k/gi, 'fifteen thousand pounds');
    
    // £100K -> "one hundred thousand pounds"
    naturalText = naturalText.replace(/£100K/gi, 'one hundred thousand pounds');
    naturalText = naturalText.replace(/£100k/gi, 'one hundred thousand pounds');
    
    // Generic pattern for other amounts (e.g., £5K -> "five thousand pounds")
    naturalText = naturalText.replace(/£(\d+)K/gi, (match, num) => {
      const number = parseInt(num);
      return `${number} thousand pounds`;
    });
    
    return naturalText;
  };

  // Voice output functions
  const speakTourStep = () => {
    if (isSpeaking) {
      stopSpeech();
      return;
    }

    window.speechSynthesis.cancel();

    const fullText = `${currentStep?.title}. ${currentStep?.content}`;
    const naturalText = convertToNaturalSpeech(fullText);
    const utterance = new SpeechSynthesisUtterance(naturalText);

    // Optimized settings for natural UK voice
    utterance.rate = 0.95;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    const voices = window.speechSynthesis.getVoices();
    
    // Prioritise high-quality UK voices
    const britishVoice = voices.find(
      (voice) => 
        (voice.lang === 'en-GB' && voice.name.includes('Google')) ||
        (voice.lang === 'en-GB' && voice.name.includes('Microsoft')) ||
        (voice.lang === 'en-GB' && voice.name.includes('Daniel')) ||
        (voice.lang === 'en-GB' && voice.name.includes('Kate')) ||
        (voice.lang === 'en-GB')
    );
    
    if (britishVoice) {
      utterance.voice = britishVoice;
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  const stopSpeech = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  // Load voices on mount
  useEffect(() => {
    const loadVoices = () => {
      window.speechSynthesis.getVoices();
    };
    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      stopSpeech();
    };
  }, []);

  // Custom next/previous handlers that animate back to optimal position
  const handleNext = () => {
    animateToOptimalPosition();
    setTimeout(nextStep, 300);
  };

  const handlePrevious = () => {
    animateToOptimalPosition();
    setTimeout(previousStep, 300);
  };

  const handleSkip = () => {
    stopSpeech();
    skipTour();
  };

  // Recalculate position on window resize and scroll
  useEffect(() => {
    if (!targetElement) return;

    const updatePosition = () => {
      calculateModalPosition(targetElement);
    };

    // Update on resize
    window.addEventListener('resize', updatePosition);
    
    // Update on scroll (both window and any scrollable parent)
    window.addEventListener('scroll', updatePosition, true);
    
    // Use requestAnimationFrame for smooth updates
    let rafId: number;
    const smoothUpdate = () => {
      updatePosition();
      rafId = requestAnimationFrame(smoothUpdate);
    };
    rafId = requestAnimationFrame(smoothUpdate);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      cancelAnimationFrame(rafId);
    };
  }, [targetElement]);

  if (!activeTour || !currentStep) return null;

  const progress = `Step ${currentStepIndex + 1} of ${activeTour.steps.length}`;

  // Get arrow styles based on position
  const getArrowStyles = () => {
    const arrowSize = 12;
    const baseStyles = {
      position: 'absolute' as const,
      width: 0,
      height: 0,
      borderStyle: 'solid' as const,
    };

    switch (arrowPosition) {
      case 'top':
        return {
          ...baseStyles,
          top: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: `0 ${arrowSize}px ${arrowSize}px ${arrowSize}px`,
          borderColor: `transparent transparent hsl(var(--card)) transparent`,
        };
      case 'bottom':
        return {
          ...baseStyles,
          bottom: -arrowSize,
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: `${arrowSize}px ${arrowSize}px 0 ${arrowSize}px`,
          borderColor: `hsl(var(--card)) transparent transparent transparent`,
        };
      case 'left':
        return {
          ...baseStyles,
          left: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize}px ${arrowSize}px ${arrowSize}px 0`,
          borderColor: `transparent hsl(var(--card)) transparent transparent`,
        };
      case 'right':
        return {
          ...baseStyles,
          right: -arrowSize,
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: `${arrowSize}px 0 ${arrowSize}px ${arrowSize}px`,
          borderColor: `transparent transparent transparent hsl(var(--card))`,
        };
    }
  };

  return (
    <>
      {/* Dimmed overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-[9998] transition-opacity duration-300"
        onClick={skipTour}
      />

      {/* Spotlight effect on target element */}
      {targetElement && (
        <>
          {/* Bright overlay for target element */}
          <div
            className="fixed z-[9999] pointer-events-none transition-all duration-300"
            style={{
              top: targetElement.getBoundingClientRect().top - 4,
              left: targetElement.getBoundingClientRect().left - 4,
              width: targetElement.getBoundingClientRect().width + 8,
              height: targetElement.getBoundingClientRect().height + 8,
              boxShadow: '0 0 0 4px hsl(var(--chart-blue) / 0.5), 0 0 20px 8px hsl(var(--chart-blue) / 0.3)',
              borderRadius: '8px',
              background: 'transparent',
            }}
          />
          {/* Make target element appear above dimmed overlay */}
          <style>
            {`
              ${currentStep.target} {
                position: relative !important;
                z-index: 9999 !important;
              }
            `}
          </style>
        </>
      )}

      {/* Tour modal */}
      <Card
        ref={modalRef}
        className={cn(
          'fixed z-[10000] w-[400px] transition-all duration-300',
          isDragging ? 'shadow-2xl' : 'shadow-xl'
        )}
        style={
          targetElement
            ? {
                top: `${modalPosition.top}px`,
                left: `${modalPosition.left}px`,
                transform: 'none',
              }
            : {
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }
        }
      >
        {/* Arrow pointing to target */}
        {targetElement && (
          <div style={getArrowStyles()} />
        )}

        <CardHeader 
          className={cn('pb-3 tour-header', isDragging ? 'cursor-grabbing' : 'cursor-grab')}
          onMouseDown={handleHeaderMouseDown}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Badge variant="secondary" className="mb-2">
                {progress}
              </Badge>
              <CardTitle className="text-lg">{currentStep.title}</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 -mt-1 -mr-1"
              onClick={handleSkip}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
            {currentStep.content}
          </p>
          
          {/* Voice button */}
          <div className="mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={speakTourStep}
              className="gap-2 w-full"
            >
              {isSpeaking ? (
                <>
                  <VolumeX className="h-4 w-4" />
                  Stop Reading
                </>
              ) : (
                <>
                  <Volume2 className="h-4 w-4" />
                  Listen to This Step
                </>
              )}
            </Button>
          </div>

          <div className="flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSkip}
            >
              Skip Tour
            </Button>
            <div className="flex gap-2">
              {currentStepIndex > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePrevious}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              <Button size="sm" onClick={handleNext}>
                {currentStepIndex === activeTour.steps.length - 1 ? 'Finish' : 'Next'}
                {currentStepIndex < activeTour.steps.length - 1 && (
                  <ArrowRight className="h-4 w-4 ml-1" />
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
